import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import ProjectCard from '../components/ProjectCard';
import { useIsAuthenticated } from '@azure/msal-react';

const CATEGORIES = ['Innovation', 'Execution'];

interface VoteType {
  category: string;
  project_id: number;
}

interface ProjectType {
  id: number;
  title: string;
  description: string;
  team_members: string[] | string;
  nominatedCategories?: string[];
}

interface LabDayType {
  id: number;
  slug: string;
  title: string;
  description?: string;
  status: string;
  projects?: ProjectType[];
}

const Voting = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const [labDay, setLabDay] = useState<LabDayType | null>(null);
  const [projects, setProjects] = useState<ProjectType[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [votes, setVotes] = useState<Record<string, number>>({});

  useEffect(() => {
    fetchLabDay();

    // Polling mechanism to check for lab day status changes
    const pollInterval = setInterval(async () => {
      try {
        // Only poll if we have a slug
        if (slug) {
          // Reduced logging - only log in development
          if (import.meta.env.DEV) {
            console.debug('Polling for lab day status');
          }
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
          const response = await axios.get(`${apiUrl}/api/lab-days/${slug}`);
          
          // Store current status for comparison
          const currentStatus = labDay?.status;
          
          // If status changed to closed, redirect back to lab day page
          if (response.data.status === 'closed' && currentStatus !== 'closed') {
            console.log('Polling detected voting ended');
            toast.info('Voting has ended!');
            navigate(`/lab/${slug}`);
          }
        }
      } catch (error) {
        console.error('Error polling lab day status:', error);
      }
    }, 5000); // Poll every 5 seconds

    // Only clean up when component unmounts or slug changes
    return () => {
      // Reduced logging
      if (import.meta.env.DEV) {
        console.debug('Clearing polling interval');
      }
      clearInterval(pollInterval);
    };
    
    // Only re-run when slug or navigate function changes
    // Explicitly NOT including labDay in dependencies to prevent frequent re-runs
  }, [slug, navigate]);

  const fetchLabDay = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await axios.get(`${apiUrl}/api/lab-days/${slug}`);
      
      // Check if voting has already ended and redirect immediately if needed
      if (response.data.status === 'closed') {
        console.log('Voting has already ended, redirecting back to lab day page');
        setIsLoading(true); // Keep loading state while redirecting
        toast.info('Voting has ended! Redirecting back to Lab Day...');
        setTimeout(() => {
          navigate(`/lab/${slug}`);
        }, 500); // Small delay for toast to show
        return; // Stop further processing
      }
      
      setLabDay(response.data);
      setProjects(response.data.projects || []);
      
      // Fetch user's votes
      try {
        const votesResponse = await axios.get(`${apiUrl}/api/lab-days/${slug}/votes`);
        const userVotes: Record<string, number> = {};
        votesResponse.data.forEach((vote: VoteType) => {
        userVotes[vote.category] = vote.project_id;
      });
      setVotes(userVotes);
      } catch (voteError) {
        console.error('Error fetching votes:', voteError);
      }
    } catch (error) {
      toast.error('Failed to load voting data');
      console.error('Error fetching voting data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVote = async (projectId: number, category: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      await axios.post(`${apiUrl}/api/lab-days/${slug}/vote`, {
        project_id: projectId,
        category
      });

      setVotes({ ...votes, [category]: projectId });
      toast.success(`Vote recorded for ${category}`);
    } catch (error) {
      toast.error('Failed to record vote');
      console.error('Error recording vote:', error);
    }
  };

  const endVoting = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      await axios.post(`${apiUrl}/api/lab-days/${slug}/end-voting`);
      navigate(`/lab/${slug}`);
      toast.success('Voting phase ended!');
    } catch (error) {
      toast.error('Failed to end voting phase');
      console.error('Error ending voting:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shopware-blue mb-4"></div>
        <p className="text-shopware-dark-blue text-xl">Loading voting data...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-shopware-dark-blue">
            Vote for Projects
          </h1>
          <p className="text-gray-600 mt-2">
            Vote for your favorite project in each category
          </p>
        </div>
        {isAuthenticated && (
          <button
            onClick={endVoting}
            className="bg-shopware-blue text-white px-4 py-2 rounded-xl hover:bg-shopware-dark-blue transition-all"
          >
            Stop Voting
          </button>
        )}
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects
          .filter(project => Array.isArray(project.nominatedCategories) && project.nominatedCategories.length > 0)
          .map((project, index) => (
            <ProjectCard
              key={index}
              project={project}
              onVote={handleVote}
              onNominate={async () => {}}
              isVotingPhase={labDay?.status === 'voting'}
              labDayStatus={labDay?.status || 'voting'}
              slug={slug || ''}
              userVotes={votes}
            />
          ))}
      </div>
    </div>
  );
};

export default Voting; 